// app/auth/services_and_hooks/authService.ts
'use client';

import { type AuthChangeEvent, type Session } from '@supabase/supabase-js';

import { posthogClientService } from '@/app/features/posthog/services_and_hooks/posthogClientService';
import { type Database } from '@/types/supabase';
import { createClient } from '@/utils/supabase/client';

import { 
  AuthErrorCode,
  type AuthResponse, 
  type LoginCredentials, 
  type ResetPasswordCredentials, 
  SignupCredentials,
  type UpdatePasswordCredentials,
  type UserProfile} from '../auth.interface';
import { useAuthStore } from './authStore';

/**
 * Auth service with functions for authentication
 */
export const authService = {
  onAuthStateChange(
    callback: (event: AuthChangeEvent, session: Session | null) => void
  ) {
    const supabase = createClient();
    return supabase.auth.onAuthStateChange(callback);
  },


  /**
   * Sign in a user with email and password
   */

  // v2

  // app/auth/services_and_hooks/authService.ts

  /**
   * Sign in a user with email and password.
   * GRACEFUL FIX: If the email is not confirmed, it will resend the OTP
   * and redirect to the verification page instead of failing.
   */
  async signIn({ email, password }: LoginCredentials): Promise<AuthResponse> {
    try {
      console.log(`Signing in user with email: ${email}`);
      
      const supabase = createClient();
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
  
      if (error) {
        // --- START OF GRACEFUL FIX LOGIC ---
        if (error.message.toLowerCase().includes('email not confirmed')) {
          console.log('[signIn] User email not confirmed. Initiating OTP resend...');
          
          // Attempt to resend the verification code. We re-use the resendOtp logic here.
          // This call already has its own robust error handling for rate-limiting.
          const resendResult = await this.resendOtp({ email });

          if (resendResult.error) {
            console.error('[signIn] Failed to resend OTP during sign-in attempt:', resendResult.error);
            // Pass the specific error from the resend attempt (e.g., rate limit message) back to the form.
            return { error: resendResult.error };
          }

          console.log('[signIn] OTP resent successfully. Redirecting to verification page.');
          // Redirect the user to the page where they can enter the new code.
          if (typeof window !== 'undefined') {
            window.location.href = `/auth/verify-email?email=${encodeURIComponent(email)}`;
          }

          // Return a non-error response to prevent the form from showing a failure state while it redirects.
          return { data: { message: 'Redirecting to email verification...' } };
        }
        // --- END OF GRACEFUL FIX LOGIC ---
  
        // For all other errors, behave as normal.
        console.error('Supabase signIn error:', error);
        return {
          error: {
            message: error.message,
            code: mapErrorCodeToAuthErrorCode(error.message),
          },
        };
      }
  
      // Critical: Update auth store directly after successful sign-in
      if (data?.user && data?.session) {
        useAuthStore.setState({
          user: data.user,
          session: data.session,
          isAuthenticated: true,
          isLoading: false
        });

        // Identify user in PostHog for existing users signing in
        posthogClientService.identify(data.user.id, {
          email: data.user.email || email,
          login_method: 'email',
        });
      }
  
      return { data };
    } catch (error: any) {
      console.error('Sign in error:', error);
      return {
        error: {
          message: error.message || 'An error occurred during sign in',
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },

  // v1
  // async signIn({ email, password }: LoginCredentials): Promise<AuthResponse> {
  //   try {
  //     console.log(`Signing in user with email: ${email}`);
      
  //     const supabase = createClient();
  //     const { data, error } = await supabase.auth.signInWithPassword({
  //       email,
  //       password,
  //     });
  
  //     console.log('Supabase signIn response:', { 
  //       success: !error, 
  //       user: data?.user ? 'User exists' : 'No user', 
  //       session: data?.session ? 'Session exists' : 'No session',
  //       error: error ? error.message : 'none'
  //     });
  
  //     if (error) {
  //       console.error('Supabase signIn error:', error);
  //       return {
  //         error: {
  //           message: error.message,
  //           code: mapErrorCodeToAuthErrorCode(error.message),
  //         },
  //       };
  //     }
  
  //     // Critical: Update auth store directly after successful sign-in
  //     try {
  //       if (data?.user && data?.session) {
  //         useAuthStore.setState({
  //           user: data.user,
  //           session: data.session,
  //           isAuthenticated: true,
  //           isLoading: false
  //         });
  //       }
  //     } catch (storeError) {
  //       console.error('Error updating auth store after sign in:', storeError);
  //     }
  
  //     return { data };
  //   } catch (error: any) {
  //     console.error('Sign in error:', error);
  //     return {
  //       error: {
  //         message: error.message || 'An error occurred during sign in',
  //         code: AuthErrorCode.UNKNOWN_ERROR,
  //       },
  //     };
  //   }
  // },

  /**
   * Sign up a new user with email and password using OTP
   */


  // v2
  /**
 * Sign up a new user with email and password using OTP
 */
async signUp({ email, password }: SignupCredentials): Promise<AuthResponse> {
  try {
    console.log(`Signing up user with email: ${email}`);
    
    const supabase = createClient();
    
    // Create the user account with password
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/confirm`,
      },
    });

    if (signUpError) {
      console.error('Supabase signUp error:', signUpError);
      return {
        error: {
          message: signUpError.message,
          code: mapErrorCodeToAuthErrorCode(signUpError.message),
        },
      };
    }

    // Handle specific case: User already exists but hasn't confirmed email
    if (signUpData?.user?.identities?.length === 0) {
      console.log('User exists but email not confirmed. Redirecting to verification...');
      
      // Redirect to verification page - let user use resend button there if needed
      if (typeof window !== 'undefined') {
        window.location.href = `/auth/verify-email?email=${encodeURIComponent(email)}`;
      }
      return { data: { user: signUpData.user, session: null } }; 
    }

    // If signup is successful AND email confirmation is OFF (session exists)
    if (signUpData.session && signUpData.user) {
      console.log('Signup successful and email confirmation disabled. Updating store and redirecting to conversations.');
      
      // Track successful signup in PostHog
      posthogClientService.track('user_signed_up', {
        user_id: signUpData.user.id,
        email: signUpData.user.email || email,
        signup_method: 'email',
      });
      
      // CHECK FOR REFERRAL CODE
      const referralCode = typeof window !== 'undefined' ? localStorage.getItem('referral_code') : null;
      if (referralCode) {
        console.log('[Auth] Recording referral signup for code:', referralCode);
        try {
          await fetch('/api/referrals/signup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ referral_code: referralCode }),
          });
          // Clear after successful attribution
          if (typeof window !== 'undefined') {
            localStorage.removeItem('referral_code');
          }
          console.log('[Auth] Referral recorded successfully');
        } catch (error) {
          console.error('[Auth] Failed to record referral:', error);
          // Don't block signup flow if referral recording fails
        }
      }
      
      // Update store immediately
      useAuthStore.setState({
        user: signUpData.user,
        session: signUpData.session,
        isAuthenticated: true,
        isLoading: false,
      });
      
      // Auto-assign user to organization based on email domain (fire and forget)
      if (signUpData.user?.email) {
        fetch('/api/auth/auto-assign-organization', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }).catch((error) => {
          console.error('[Auth] Failed to auto-assign organization:', error);
          // Don't block user flow if auto-assignment fails
        });
      }
      
      // Redirect to conversations (onboarding disabled)
      if (typeof window !== 'undefined') {
        window.location.href = '/conversations';
      }
      return { data: signUpData };
    }

    // If signup is successful AND email confirmation is ON (no session yet)
    if (!signUpData.session && signUpData.user) {
      console.log('Signup successful, email confirmation enabled. Supabase already sent confirmation email.');
      
      // Supabase already sent a confirmation email via the signUp call above
      // No need to send another OTP - just redirect to verification page
      if (typeof window !== 'undefined') {
        window.location.href = `/auth/verify-email?email=${encodeURIComponent(email)}`;
      }
      return { data: { user: signUpData.user, session: null } }; 
    }

    // Fallback for unexpected state
    console.warn('Unexpected state after signup attempt.');
    return { 
      error: { message: 'Unexpected state after signup.', code: AuthErrorCode.UNKNOWN_ERROR } 
    };

  } catch (error: any) {
    console.error('Sign up error caught in service:', error);
    return {
      error: {
        message: error.message || 'An error occurred during sign up',
        code: AuthErrorCode.UNKNOWN_ERROR,
      },
    };
  }
},

  
  // v1
  // async signUp({ email, password }: SignupCredentials): Promise<AuthResponse> {
  //   try {
  //     console.log(`Signing up user with email: ${email}`);
      
  //     const supabase = createClient();
      
  //     // First, create the user account with password
  //     const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
  //       email,
  //       password,
  //       options: {
  //         emailRedirectTo: `${window.location.origin}/auth/confirm`,
  //       },
  //     });

  //     if (signUpError) {
  //       console.error('Supabase signUp error:', signUpError);
  //       return {
  //         error: {
  //           message: signUpError.message,
  //           code: mapErrorCodeToAuthErrorCode(signUpError.message),
  //         },
  //       };
  //     }

  //     // Handle specific error: User already exists but hasn't confirmed email
  //     if (signUpData?.user?.identities?.length === 0) {
  //       console.log('User exists but email not confirmed. Sending OTP...');
  //       // Send OTP for existing unconfirmed user
  //       const { error: otpError } = await supabase.auth.signInWithOtp({
  //         email,
  //         options: {
  //           shouldCreateUser: false,
  //         },
  //       });

  //       if (otpError) {
  //         console.error('OTP send error:', otpError);
  //         return {
  //           error: {
  //             message: otpError.message,
  //             code: mapErrorCodeToAuthErrorCode(otpError.message),
  //           },
  //         };
  //       }

  //       // Redirect to verification page
  //       if (typeof window !== 'undefined') {
  //         window.location.href = `/auth/verify-email?email=${encodeURIComponent(email)}`;
  //       }
  //       return { data: { user: signUpData.user, session: null } }; 
  //     }

  //     // If signup is successful AND email confirmation is OFF (session exists)
  //     if (signUpData.session && signUpData.user) {
  //       console.log('Signup successful and email confirmation likely disabled. Updating store and redirecting to onboarding.');
  //       // Update store immediately
  //       useAuthStore.setState({
  //         user: signUpData.user,
  //         session: signUpData.session,
  //         isAuthenticated: true,
  //         isLoading: false,
  //       });
  //       // Redirect to onboarding
  //       if (typeof window !== 'undefined') {
  //         window.location.href = '/onboarding/basic-info';
  //       }
  //       return { data: signUpData }; // Return successful data with session
  //     }

  //     // If signup is successful AND email confirmation is ON (no session yet)
  //     if (!signUpData.session && signUpData.user) {
  //       console.log('Signup successful, sending OTP for email verification...');
        
  //       // Send OTP for email verification
  //       const { error: otpError } = await supabase.auth.signInWithOtp({
  //         email,
  //         options: {
  //           shouldCreateUser: false, // User already created
  //         },
  //       });

  //       if (otpError) {
  //         console.error('OTP send error:', otpError);
  //         return {
  //           error: {
  //             message: otpError.message,
  //             code: mapErrorCodeToAuthErrorCode(otpError.message),
  //           },
  //         };
  //       }

  //       // Redirect to verification page
  //       if (typeof window !== 'undefined') {
  //         window.location.href = `/auth/verify-email?email=${encodeURIComponent(email)}`;
  //       }
  //       // Return user data but no session, indicating pending confirmation
  //       return { data: { user: signUpData.user, session: null } }; 
  //     }

  //     // Fallback for unexpected state (should not happen)
  //     console.warn('Unexpected state after signup attempt.');
  //     return { 
  //       error: { message: 'Unexpected state after signup.', code: AuthErrorCode.UNKNOWN_ERROR } 
  //     };

  //   } catch (error: any) {
  //     console.error('Sign up error caught in service:', error);
  //     return {
  //       error: {
  //         message: error.message || 'An error occurred during sign up',
  //         code: AuthErrorCode.UNKNOWN_ERROR,
  //       },
  //     };
  //   }
  // },

  /**
   * Verify OTP code for email verification
   */
  async verifyOtp({ email, token }: { email: string; token: string }): Promise<AuthResponse> {
    try {
      console.log(`Verifying OTP for email: ${email}`);
      
      const supabase = createClient();
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });

      if (error) {
        console.error('OTP verification error:', error);
        return {
          error: {
            message: error.message,
            code: mapErrorCodeToAuthErrorCode(error.message),
          },
        };
      }

      if (data.session && data.user) {
        console.log('OTP verification successful. Updating store and redirecting to conversations.');
        
        // Track successful signup (email verification completed)
        posthogClientService.track('user_signed_up', {
          user_id: data.user.id,
          email: data.user.email || email,
          signup_method: 'email',
        });
        
        // Update store immediately
        useAuthStore.setState({
          user: data.user,
          session: data.session,
          isAuthenticated: true,
          isLoading: false,
        });
        
        // Auto-assign user to organization based on email domain (fire and forget)
        if (data.user?.email) {
          fetch('/api/auth/auto-assign-organization', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          }).catch((error) => {
            console.error('[Auth] Failed to auto-assign organization:', error);
            // Don't block user flow if auto-assignment fails
          });
        }
        
        // Redirect to conversations (onboarding disabled)
        if (typeof window !== 'undefined') {
          window.location.href = '/conversations';
        }
      }

      return { data };
    } catch (error: any) {
      console.error('OTP verification error caught in service:', error);
      return {
        error: {
          message: error.message || 'An error occurred during OTP verification',
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },

  /**
   * Resend OTP code
   */
/**
 * Resend OTP code with better rate limiting handling
 */
async resendOtp({ email }: { email: string }): Promise<AuthResponse> {
  try {
    console.log(`Resending OTP for email: ${email}`);
    
    const supabase = createClient();
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
      },
    });

    if (error) {
      console.error('Resend OTP error:', error);
      
      // Handle rate limiting specifically
      if (error.message.includes('you can only request this after')) {
        // Extract the number of seconds from the error message
        const match = error.message.match(/(\d+) seconds/);
        const waitTime = match ? parseInt(match[1]) : 60;
        
        return {
          error: {
            message: `Please wait ${waitTime} seconds before requesting another code. You can also check your email for the previous verification code.`,
            code: AuthErrorCode.TOO_MANY_ATTEMPTS,
          },
        };
      }
      
      return {
        error: {
          message: error.message,
          code: mapErrorCodeToAuthErrorCode(error.message),
        },
      };
    }

    return { data: true };
  } catch (error: any) {
    console.error('Resend OTP error caught in service:', error);
    return {
      error: {
        message: error.message || 'An error occurred while resending OTP',
        code: AuthErrorCode.UNKNOWN_ERROR,
      },
    };
  }
},

  /**
   * Sign out the current user
   */
  async signOut(): Promise<AuthResponse> {
    try {
      console.log('Initiating sign out process...');
      
      // Only call our server API endpoint to handle signout server-side
      const response = await fetch('/api/auth/signout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Server-side signout failed:', response.status, await response.text());
        throw new Error('Server failed to sign out');
      }
      
      console.log('Server-side signout successful.');
      
      // Reset PostHog user identification
      posthogClientService.reset();
      
      // Force a hard reload to clear all client state and redirect
      if (typeof window !== 'undefined') {
        console.log('Redirecting to /login?logout=success');
        window.location.href = '/login?logout=success';
      }

      // Technically, the redirect means this won't be reached, 
      // but returning success for completeness.
      return { data: true };

    } catch (error: any) {
      console.error('Sign out error:', error);
      
      // If anything fails, attempt a hard redirect to login with an error param
      if (typeof window !== 'undefined') {
        console.log('Redirecting to /login?error=signout_failed due to error');
        window.location.href = '/login?error=signout_failed';
      }
      
      return {
        error: {
          message: error.message || 'An error occurred during sign out',
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },

  /**
   * Reset password with email
   */
  async resetPassword({ email }: ResetPasswordCredentials): Promise<AuthResponse> {
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
      });

      if (error) {
        return {
          error: {
            message: error.message,
            code: mapErrorCodeToAuthErrorCode(error.message),
          },
        };
      }

      return { data: true };
    } catch (error: any) {
      return {
        error: {
          message: error.message || 'An error occurred during password reset',
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },

  /**
   * Update password for the current user
   */
  async updatePassword({ password }: UpdatePasswordCredentials): Promise<AuthResponse> {
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) {
        return {
          error: {
            message: error.message,
            code: mapErrorCodeToAuthErrorCode(error.message),
          },
        };
      }

      return { data: true };
    } catch (error: any) {
      return {
        error: {
          message: error.message || 'An error occurred during password update',
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },

  /**
   * Get the current user session
   */
  async getSession(): Promise<AuthResponse> {
    try {
      const supabase = createClient();
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        return {
          error: {
            message: error.message,
            code: mapErrorCodeToAuthErrorCode(error.message),
          },
        };
      }

      return { data: data.session };
    } catch (error: any) {
      return {
        error: {
          message: error.message || 'An error occurred getting session',
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },

  /**
   * Get the current user
   */
  async getUser(): Promise<AuthResponse> {
    try {
      const supabase = createClient();
      const { data, error } = await supabase.auth.getUser();

      if (error) {
        return {
          error: {
            message: error.message,
            code: AuthErrorCode.UNKNOWN_ERROR,
          },
        };
      }

      return { data: { user: data.user, session: null } };
    } catch (error: any) {
      return {
        error: {
          message: error.message || 'An error occurred while fetching the user',
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },

  /**
   * Get user profile from the user_profiles table
   */


  async getUserProfile(userId: string): Promise<AuthResponse<UserProfile | null>> {
    console.log(`[authService] Getting user profile for user: ${userId}`);
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error(`[authService] Supabase error fetching profile for user ${userId}:`, error);
        return {
          data: null,
          error: {
            message: error.message,
            code: AuthErrorCode.DATABASE_ERROR,
          },
        };
      }
      
      if (data) {
        console.log(`[authService] Successfully fetched profile for user ${userId}.`);
      } else {
        console.warn(`[authService] No profile found for user ${userId}, returning null.`);
      }

      // *** THE FIX IS HERE ***
      // On success, return the data and explicitly set error to undefined or omit it.
      // Returning just the data property is the cleanest way.
      return { data: data as UserProfile | null };

    } catch (error: any) {
      console.error(`[authService] Catch block error fetching profile for user ${userId}:`, error);
      return {
        data: null,
        error: {
          message: error.message || 'An error occurred while fetching the user profile',
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },

  async createUserProfile(
    profile: Database['public']['Tables']['user_profiles']['Insert']
  ): Promise<AuthResponse<UserProfile>> {
    try {
      const supabase = createClient();
      const { data, error } = await (supabase
        .from('user_profiles') as any)
        .insert(profile)
        .select()
        .single();

      if (error) {
        return {
          error: {
            message: error.message,
            code: AuthErrorCode.UNKNOWN_ERROR,
          },
        };
      }

      return { data };
    } catch (error: any) {
      return {
        error: {
          message: error.message || 'An error occurred creating user profile',
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },

  /**
   * Update a user profile
   */
  async updateUserProfile(
    userId: string,
    updates: Database['public']['Tables']['user_profiles']['Update']
  ): Promise<AuthResponse<UserProfile>> {
    try {
      const supabase = createClient();
      const { data, error } = await (supabase
        .from('user_profiles') as any)
        .update(updates)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        return {
          error: {
            message: error.message,
            code: AuthErrorCode.UNKNOWN_ERROR,
          },
        };
      }

      return { data };
    } catch (error: any) {
      return {
        error: {
          message: error.message || 'An error occurred updating user profile',
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },
  
  /**
   * Force sign out - emergency reset
   */
  async forceSignOut(): Promise<AuthResponse> {
    try {
      console.log("[EMERGENCY] Force sign out initiated");
      
      // Aggressively clear client-side storage first
      if (typeof window !== 'undefined') {
        console.log("[EMERGENCY] Clearing localStorage...");
        localStorage.clear();
        console.log("[EMERGENCY] Clearing sessionStorage...");
        sessionStorage.clear();
        
        // Attempt cookie clearing (might not work for HttpOnly)
        console.log("[EMERGENCY] Attempting cookie clearing...");
        document.cookie.split(';').forEach(c => {
          document.cookie = c
            .replace(/^ +/, '')
            .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
        });
      }
      
      // Then, attempt server-side sign out via API
      try {
        console.log("[EMERGENCY] Calling server-side signout endpoint...");
        await fetch('/api/auth/signout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (apiError) {
        console.warn("[EMERGENCY] Server endpoint call failed, proceeding with redirect:", apiError);
      }
      
      // Regardless of API call success, force redirect
      if (typeof window !== 'undefined') {
        console.log("[EMERGENCY] Redirecting to /login?force_reset=true");
        window.location.href = '/login?force_reset=true';
      }
      
      return { data: true }; // Unlikely to be reached
    } catch (error: any) {
      console.error('[EMERGENCY] Error during force sign out:', error);
      // Still attempt final redirect even if client-side clearing failed
      if (typeof window !== 'undefined') {
        console.log("[EMERGENCY] Redirecting to /login?force_reset=true after error");
        window.location.href = '/login?force_reset=true';
      }
      return {
        error: {
          message: error.message || 'An error occurred during emergency sign out',
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },
  
  /**
   * Sign in with a third-party provider
   */
  async signInWithProvider(
    provider: 'google' | 'github' | 'facebook' | 'twitter',
    redirectedFrom?: string | null
  ): Promise<AuthResponse> {
    try {
      // Build the redirect URL with the redirectedFrom parameter if it exists
      let redirectUrl = `${window.location.origin}/auth/callback`;
      
      // Add redirectedFrom param if provided
      if (redirectedFrom) {
        redirectUrl += `?redirectedFrom=${encodeURIComponent(redirectedFrom)}`;
      }
      
      const supabase = createClient();
      
      // Enhanced OAuth options to address Google's "Use secure browsers" policy
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectUrl,
          // Additional options to help with Google OAuth reliability
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          // Ensure we're using PKCE flow for better security
          skipBrowserRedirect: false,
        },
      });
      
      if (error) {
        console.error(`Error during OAuth sign-in with ${provider}:`, error);
        
        // Enhanced error handling for Google OAuth specific issues
        if (provider === 'google' && error.message.includes('popup')) {
          return {
            error: {
              message: 'Please disable popup blockers and try again. If the issue persists, try using a different browser.',
              code: AuthErrorCode.NETWORK_ERROR,
            },
          };
        }
        
        return {
          error: {
            message: error.message,
            code: mapErrorCodeToAuthErrorCode(error.message),
          },
        };
      }
      
      return { data: true };
    } catch (error: any) {
      console.error(`Provider sign-in error for ${provider}:`, error);
      return {
        error: {
          message: error.message || `An error occurred during ${provider} sign in`,
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },
  
  /**
   * Refreshes the session and updates the auth state
   */
  async refreshSession(): Promise<AuthResponse> {
    try {
      console.log('[Auth Service] Refreshing session');
      const supabase = createClient();
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('[Auth Service] Error refreshing session:', error);
        return { 
          error: {
            message: error.message,
            code: mapErrorCodeToAuthErrorCode(error.message),
          },
        };
      }
      
      console.log('[Auth Service] Session refreshed successfully');
      return { data };
    } catch (error: any) {
      return {
        error: {
          message: error.message || 'An error occurred refreshing session',
          code: AuthErrorCode.UNKNOWN_ERROR,
        },
      };
    }
  },
};

/**
 * Map error messages to auth error codes
 */
/**
 * Map error messages to auth error codes
 */
function mapErrorCodeToAuthErrorCode(errorMessage: string): AuthErrorCode {
  const lowerCaseMessage = errorMessage.toLowerCase();

  if (
    lowerCaseMessage.includes('invalid login credentials') ||
    lowerCaseMessage.includes('invalid email or password')
  ) {
    return AuthErrorCode.INVALID_CREDENTIALS;
  }

  if (lowerCaseMessage.includes('user already registered') || 
      lowerCaseMessage.includes('email address is already in use')) {
    return AuthErrorCode.EMAIL_ALREADY_EXISTS;
  }

  if (
    lowerCaseMessage.includes('password should be at least') ||
    lowerCaseMessage.includes('password')
  ) {
    return AuthErrorCode.WEAK_PASSWORD;
  }

  if (lowerCaseMessage.includes('user not found')) {
    return AuthErrorCode.USER_NOT_FOUND;
  }

  // Handle rate limiting errors
  if (lowerCaseMessage.includes('too many attempts') || 
      lowerCaseMessage.includes('you can only request this after') ||
      lowerCaseMessage.includes('for security purposes')) {
    return AuthErrorCode.TOO_MANY_ATTEMPTS;
  }

  if (lowerCaseMessage.includes('expired')) {
    return AuthErrorCode.EXPIRED_CODE;
  }

  if (lowerCaseMessage.includes('invalid code')) {
    return AuthErrorCode.INVALID_CODE;
  }

  if (lowerCaseMessage.includes('network')) {
    return AuthErrorCode.NETWORK_ERROR;
  }

  return AuthErrorCode.UNKNOWN_ERROR;
}